/**
 * Language Store
 *
 * Zustand store for language/i18n state management.
 * Supports SK, CZ, EN languages with browser detection and local JSON translations.
 * Syncs language preference with backend for logged-in users.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authApi } from '@/lib/api';

// Import translations
import skTranslations from '@/locales/sk.json';
import enTranslations from '@/locales/en.json';
import csTranslations from '@/locales/cs.json';

// Available languages
export type LanguageCode = 'sk' | 'cs' | 'en';

export interface Language {
  code: LanguageCode;
  name: string;
  nativeName: string;
  flag: string;
}

export const AVAILABLE_LANGUAGES: Language[] = [
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', flag: '🇸🇰' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
];

// Translation type - nested object structure
type TranslationValue = string | { [key: string]: TranslationValue };
export type Translations = { [key: string]: TranslationValue };

// All translations
const allTranslations: Record<LanguageCode, Translations> = {
  sk: skTranslations,
  cs: csTranslations,
  en: enTranslations,
};

// Detect browser language
const detectBrowserLanguage = (): LanguageCode => {
  if (typeof window === 'undefined') return 'sk';
  
  const browserLang = navigator.language.toLowerCase();
  
  if (browserLang.startsWith('sk')) return 'sk';
  if (browserLang.startsWith('cs') || browserLang.startsWith('cz')) return 'cs';
  if (browserLang.startsWith('en')) return 'en';
  
  // Default to Slovak
  return 'sk';
};

// Get nested value from object using dot notation
const getNestedValue = (obj: Translations, path: string): string | undefined => {
  if (!obj || typeof obj !== 'object') return undefined;
  const keys = path.split('.');
  let current: TranslationValue = obj;
  
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined;
    const next: TranslationValue = (current as { [key: string]: TranslationValue })[key];
    if (next === undefined) return undefined;
    current = next;
  }
  
  return typeof current === 'string' ? current : undefined;
};

// Language state type
interface LanguageState {
  // State
  currentLanguage: LanguageCode;
  isLoading: boolean;
  isInitialized: boolean;
  isSyncing: boolean;
  
  // Actions
  setLanguage: (code: LanguageCode, syncToBackend?: boolean) => void;
  setLanguageFromBackend: (code: LanguageCode) => void;
  initLanguage: () => void;
  syncLanguageToBackend: (code: LanguageCode) => Promise<void>;
  loadLanguageFromBackend: () => Promise<void>;
  t: (key: string, params?: Record<string, string | number>, fallback?: string) => string;
  getLanguage: () => Language;
}

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentLanguage: 'sk',
      isLoading: false,
      isInitialized: false,
      isSyncing: false,
      
      // Initialize language (detect from browser if not set)
      initLanguage: () => {
        const { isInitialized } = get();
        
        if (!isInitialized) {
          // Check if we have a stored language preference
          const stored = localStorage.getItem('language-storage');
          if (!stored) {
            // No stored preference, detect from browser
            const detectedLang = detectBrowserLanguage();
            set({ currentLanguage: detectedLang, isInitialized: true });
          } else {
            set({ isInitialized: true });
          }
        }
        
        // Update HTML lang attribute
        if (typeof document !== 'undefined') {
          document.documentElement.lang = get().currentLanguage;
        }
      },
      
      // Set language from backend (without syncing back)
      setLanguageFromBackend: (code: LanguageCode) => {
        set({ currentLanguage: code });
        
        // Update HTML lang attribute
        if (typeof document !== 'undefined') {
          document.documentElement.lang = code;
        }
        
        // Update localStorage for API interceptor
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('language', code);
        }
      },
      
      // Set language (with optional sync to backend)
      setLanguage: (code: LanguageCode, syncToBackend: boolean = true) => {
        set({ currentLanguage: code });
        
        // Update HTML lang attribute
        if (typeof document !== 'undefined') {
          document.documentElement.lang = code;
        }
        
        // Update localStorage for API interceptor
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('language', code);
        }
        
        // Sync with backend for logged-in users
        if (syncToBackend) {
          get().syncLanguageToBackend(code);
        }
      },
      
      // Sync language preference to backend
      syncLanguageToBackend: async (code: LanguageCode) => {
        const { isSyncing } = get();
        if (isSyncing) return;
        
        set({ isSyncing: true });
        
        try {
          await authApi.updateLanguage(code);
        } catch (error) {
          // Silently fail - language is still saved locally
          console.warn('Failed to sync language to backend:', error);
        } finally {
          set({ isSyncing: false });
        }
      },
      
      // Load language preference from backend
      loadLanguageFromBackend: async () => {
        set({ isLoading: true });
        
        try {
          const response = await authApi.getLanguage();
          if (response.data.success && response.data.language) {
            const lang = response.data.language as LanguageCode;
            if (['sk', 'cs', 'en'].includes(lang)) {
              get().setLanguageFromBackend(lang);
            }
          }
        } catch (error) {
          // Silently fail - use local language
          console.warn('Failed to load language from backend:', error);
        } finally {
          set({ isLoading: false });
        }
      },
      
      // Translate function with dot notation support
      t: (key: string, params?: Record<string, string | number>, fallback?: string) => {
        const { currentLanguage } = get();
        const translations = allTranslations[currentLanguage];
        
        // Try to get translation
        let text = getNestedValue(translations, key);
        
        // Fallback to English if not found
        if (!text && currentLanguage !== 'en') {
          text = getNestedValue(allTranslations.en, key);
        }
        
        // Use fallback or key if still not found
        if (!text) {
          text = fallback || key;
        }
        
        // Replace parameters (support both {key} and {{key}} so locale strings can use either)
        if (params) {
          Object.entries(params).forEach(([paramKey, value]) => {
            const escaped = paramKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const re = new RegExp(`\\{\\{?\\s*${escaped}\\s*\\}?\\}`, 'g');
            text = text!.replace(re, String(value));
          });
        }
        
        return text;
      },
      
      // Get current language object
      getLanguage: () => {
        const { currentLanguage } = get();
        return AVAILABLE_LANGUAGES.find(l => l.code === currentLanguage) || AVAILABLE_LANGUAGES[0];
      },
    }),
    {
      name: 'language-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentLanguage: state.currentLanguage,
      }),
    }
  )
);

// Date formatting based on language
export const formatDate = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
  const { currentLanguage } = useLanguageStore.getState();
  const d = typeof date === 'string' ? new Date(date) : date;
  const locale = currentLanguage === 'sk' ? 'sk-SK' : currentLanguage === 'cs' ? 'cs-CZ' : 'en-GB';
  
  return d.toLocaleDateString(locale, options || {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Number formatting based on language
export const formatNumber = (num: number, options?: Intl.NumberFormatOptions): string => {
  const { currentLanguage } = useLanguageStore.getState();
  const locale = currentLanguage === 'sk' ? 'sk-SK' : currentLanguage === 'cs' ? 'cs-CZ' : 'en-GB';
  return num.toLocaleString(locale, options);
};

// Currency formatting based on language
export const formatCurrency = (amount: number, currency = 'EUR'): string => {
  const { currentLanguage } = useLanguageStore.getState();
  const locale = currentLanguage === 'sk' ? 'sk-SK' : currentLanguage === 'cs' ? 'cs-CZ' : 'en-GB';
  return amount.toLocaleString(locale, {
    style: 'currency',
    currency,
  });
};

// Relative time formatting
export const formatRelativeTime = (date: Date | string): string => {
  const { t } = useLanguageStore.getState();
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);
  
  if (seconds < 60) return t('time.justNow');
  if (minutes < 60) return t('time.minutesAgo', { count: minutes });
  if (hours < 24) return t('time.hoursAgo', { count: hours });
  if (days < 7) return t('time.daysAgo', { count: days });
  if (weeks < 4) return t('time.weeksAgo', { count: weeks });
  if (months < 12) return t('time.monthsAgo', { count: months });
  return t('time.yearsAgo', { count: years });
};
